import { LibData as RobotData } from "../../utils/LibRobotsData";
import { LibData as ToolData } from "../../utils/LibToolsData";
import { LibData as AxisData } from "../../utils/LibAxesData";
import { StationData } from "../../utils/StationData";
import { extracted_params, extractUrlQueryOnMessage } from "./UrlFilter";
import { useState, useEffect } from "react";


// Global variables / constatns
const size_range = {
    "Small": [0, 60],
    "Medium": [60, 250],
    "Large": [250, 9e9],
};

export const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 992); // Bootstrap lg breakpoint

    useEffect(() => {
        const update = () => setIsDesktop(window.innerWidth >= 992);
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    return isDesktop;
};

// Filter methods
const rangeFilterChangedFromDefault = (
    parameter_name: string,
    range_parameter: Array<number>
) => {
    let range_filters_default = {
        // Robot library
        "reach": [RobotData.reachMin, RobotData.reachMaxRobot],
        "payload": [RobotData.payloadMin, RobotData.payloadMaxRobot],
        "weight": [RobotData.weightMin, RobotData.weightMaxRobot],
        "repeatability": [RobotData.repeatabilityMin, RobotData.repeatabilityMaxRobot],

        // Station library
        "mechanism_counts": [StationData.robotMin, StationData.robotMax],
        "tool_counts": [StationData.toolsMin, StationData.toolsMax],
        "object_counts": [StationData.objectsMin, StationData.objectsMax],

        // Tool Library
        "tool_weight": [ToolData.weightMin, ToolData.weightMax],

        // Axis Library
        "axis_reach": [AxisData.reachMin, AxisData.reachMax],
        "axis_payload": [AxisData.payloadMin, AxisData.payloadMax],
        "axis_weight": [AxisData.weightMin, AxisData.weightMax],
        "axis_repeatability": [AxisData.repeatabilityMin, AxisData.repeatabilityMax],
    };

    return (
        range_parameter[0] > range_filters_default[parameter_name][0]
        || range_parameter[1] < range_filters_default[parameter_name][1]
    );
};

const applyFiltersToList = (
    library: string,
    parameter_name: string,
    parameter: Array<number> | any,
    items_list: any,
) => {
    switch (library) {
        case "robot":
            return applyFiltersToRobot(parameter_name, parameter, items_list);

        case "tool":
            return applyFiltersToTool(parameter_name, parameter, items_list);
        
        case "axis":
            return applyFiltersToAxis(parameter_name, parameter, items_list);

        case "object":
            return applyFiltersToObject(parameter_name, parameter, items_list);

        case "station":
            return applyFiltersToStation(parameter_name, parameter, items_list);

        case "addin":
            return applyFiltersToAddins(parameter_name, parameter, items_list);

        case "post":
            return applyFiltersToPosts(parameter_name, parameter, items_list);
    }
};

const applyFiltersToRobot = (
    parameter_name: string,
    parameter: Array<number> | any,
    robots_list: any
) => {
    let key: stringStringMap = {
        "brands": "B",
        "types": "T",
        "axes": "A",
        "reach": "R",
        "payload": "P",
        "weight": "W",
        "repeatability": "E",
        "applications": "AP",
    }
    let filtered_list = {};

    if (Array.isArray(parameter)) {
        if (rangeFilterChangedFromDefault(parameter_name, parameter)) {
            Object.entries(robots_list).map((robot) => {
                const [name, entry] = [robot[0], robot[1]];
                let [min, max] = parameter;

                if (entry[key[parameter_name]] >= min && entry[key[parameter_name]] <= max) {
                    filtered_list[name] = entry;
                }
            });
        }

    } else {
        Object.entries(robots_list).map((robot) => {
            const [name, entry] = [robot[0], robot[1]];
            if (parameter[entry[key[parameter_name]]]) {
                filtered_list[name] = entry;
            }

            if (entry.hasOwnProperty("AP")) {
                entry["AP"].map((application) => {
                    if (parameter[application] && parameter_name == "applications") {
                        filtered_list[name] = entry;
                    }
                });
            };
        });
    }

    return filtered_list;
}

const applyFiltersToTool = (
    parameter_name: string,
    parameter: Array<number> | any,
    tools_list: any
) => {
    let key: stringStringMap = {
        "brands": "B",
        "axes": "A",
        "tool_weight": "W",
        "applications": "AP",
    }
    let filtered_list = {};

    if (Array.isArray(parameter)) {
        if (rangeFilterChangedFromDefault(parameter_name, parameter)) {
            Object.entries(tools_list).map((robot) => {
                const [name, entry] = [robot[0], robot[1]];
                let [min, max] = parameter;

                if (entry[key[parameter_name]] >= min && entry[key[parameter_name]] <= max) {
                    filtered_list[name] = entry;
                }
            });
        }

    } else {
        Object.entries(tools_list).map((robot) => {
            const [name, entry] = [robot[0], robot[1]];
            if (parameter[entry[key[parameter_name]]]) {
                filtered_list[name] = entry;
            }

            if (entry.hasOwnProperty("AP")) {
                entry["AP"].map((application) => {
                    if (parameter[application] && parameter_name == "applications") {
                        filtered_list[name] = entry;
                    }
                });
            };
        });
    }

    return filtered_list;
}

const applyFiltersToAxis = (
    parameter_name: string,
    parameter: Array<number> | any,
    robots_list: any
) => {
    let key: stringStringMap = {
        "brands": "B",
        "axes": "A",
        "axis_reach": "R",
        "axis_payload": "P",
        "axis_weight": "W",
        "axis_repeatability": "E",
    }
    let filtered_list = {};

    if (Array.isArray(parameter)) {
        if (rangeFilterChangedFromDefault(parameter_name, parameter)) {
            Object.entries(robots_list).map((robot) => {
                const [name, entry] = [robot[0], robot[1]];
                let [min, max] = parameter;

                if (entry[key[parameter_name]] >= min && entry[key[parameter_name]] <= max) {
                    filtered_list[name] = entry;
                }
            });
        }

    } else {
        Object.entries(robots_list).map((robot) => {
            const [name, entry] = [robot[0], robot[1]];
            if (parameter[entry[key[parameter_name]]]) {
                filtered_list[name] = entry;
            }
        });
    }

    return filtered_list;
}

const applyFiltersToObject = (
    parameter_name: string,
    parameter: Array<number> | any,
    robots_list: any
) => {
    let key: stringStringMap = {
        "brands": "B",
        "types": "T",
        "axes": "A",
        "applications": "AP",
    }
    let filtered_list = {};

    if (Array.isArray(parameter)) {
        if (rangeFilterChangedFromDefault(parameter_name, parameter)) {
            Object.entries(robots_list).map((robot) => {
                const [name, entry] = [robot[0], robot[1]];
                let [min, max] = parameter;

                if (entry[key[parameter_name]] >= min && entry[key[parameter_name]] <= max) {
                    filtered_list[name] = entry;
                }
            });
        }

    } else {
        Object.entries(robots_list).map((robot) => {
            const [name, entry] = [robot[0], robot[1]];
            if (parameter[entry[key[parameter_name]]]) {
                filtered_list[name] = entry;
            }

            if (entry.hasOwnProperty("AP")) {
                entry["AP"].map((application) => {
                    if (parameter[application] && parameter_name == "applications") {
                        filtered_list[name] = entry;
                    }
                });
            };
        });
    }

    return filtered_list;
}

const applyFiltersToStation = (
    parameter_name: string,
    parameter: Array<number> | any,
    stations_list: any
) => {
    let key: stringStringMap = {
        "brands": "B",
        "tags": "T",
        "applications": "AP",
        "mechanism": "RT",
        "mechanism_counts": "RC",
        "tool_counts": "TC",
        "sizes": "S",
        "object_counts": "OC",
    }
    let filtered_list = {};

    if (Array.isArray(parameter)) {
        if (rangeFilterChangedFromDefault(parameter_name, parameter)) {
            Object.entries(stations_list).map((station) => {
                const [name, entry] = [station[0], station[1]];
                let [min, max] = parameter;

                if (
                    entry[key[parameter_name]] >= min &&
                    entry[key[parameter_name]] <= max
                ) {
                    filtered_list[name] = entry;
                }
            });
        }

    } else {
        Object.entries(stations_list).map((station) => {
            const [name, entry] = [station[0], station[1]];
            if (entry.hasOwnProperty("B")) {
                entry["B"].map((brand) => {
                    if (parameter[brand] && parameter_name == "brands") {
                        filtered_list[name] = entry;
                    }
                })
            };

            if (entry.hasOwnProperty("T")) {
                entry["T"].map((tag) => {
                    if (parameter[tag] && parameter_name == "tags") {
                        filtered_list[name] = entry;
                    }
                });
            };

            if (entry.hasOwnProperty("AP")) {
                entry["AP"].map((application) => {
                    if (parameter[application] && parameter_name == "applications") {
                        filtered_list[name] = entry;
                    }
                });
            };

            if (entry.hasOwnProperty("RT")) {
                entry["RT"].map((mechanism) => {
                    if (parameter[mechanism] && parameter_name == "mechanism") {
                        filtered_list[name] = entry;
                    }
                });
            };

            if (entry.hasOwnProperty("S")) {
                Object.keys(size_range).map(size => {
                    if (
                        parameter[size] &&
                        parameter_name == "sizes" &&
                        entry["S"] > size_range[size][0] &&
                        entry["S"] < size_range[size][1]
                    )
                        filtered_list[name] = entry;
                })
            }
        });
    }

    return filtered_list;
};

const applyFiltersToAddins = (
    parameter_name: string,
    parameter: Array<number> | any,
    addins_list: any
) => {
    let key: stringStringMap = {
        "publishers": "P",
        "categories": "C",
        "price": "PX",
    };
    let filtered_list = {};

    Object.entries(addins_list).map((addin) => {
        const [name, entry] = [addin[0], addin[1]];
        if (parameter[entry[key[parameter_name]]]) {
            filtered_list[name] = entry;
        }
        if (parameter_name == 'price') {
            if (parameter["Free"] && !entry.hasOwnProperty("PX")) {
                filtered_list[name] = entry
            } else if (parameter["Paid"] && entry.hasOwnProperty("PX")) {
                filtered_list[name] = entry
            }
        }
    });

    return filtered_list;
};

const applyFiltersToPosts = (
    parameter_name: string,
    parameter: Array<number> | any,
    posts_list: any
) => {
    let key: stringStringMap = {
        "brands": "B",
        "controllers": "SC",
        "extensions": "EXT"
    };
    let filtered_list = {};

    Object.entries(posts_list).map((post) => {
        const [name, entry] = [post[0], post[1]];
        switch (parameter_name) {
            case "brands":
                if (entry.hasOwnProperty('B')) {
                    if (parameter[entry[key[parameter_name]]]) {
                        filtered_list[name] = entry;
                    }
                }
                break;

            case "controllers":
                if (entry.hasOwnProperty('SC')) {
                    for (let controller of entry[key[parameter_name]]) {
                        if (parameter[controller] && !filtered_list.hasOwnProperty(name)) {
                            filtered_list[name] = entry;
                        }
                    }
                }
                break;

            case "extensions":
                if (entry.hasOwnProperty('EXT')) {
                    if (parameter[entry['EXT']]) {
                        filtered_list[name] = entry;
                    }
                }
                break;

            default:
                break;
        }
    });
    //console.log(filtered_list);

    return filtered_list;
};

const countCheckboxParameters = (
    library: string,
    items_list: any
) => {
    switch (library) {
        case "robot":
            return countRobotCheckbox(items_list);

        case "tool":
            return countToolCheckbox(items_list);

        case "axis":
            return countAxisCheckbox(items_list);

        case "object":
            return countObjectCheckbox(items_list);

        case "station":
            return countStationCheckbox(items_list);

        case "addin":
            return countAddinCheckbox(items_list);

        case "post":
            return countPostCheckbox(items_list);
    }
};

const countRobotCheckbox = (robots_list: Array<RobotDataProp>) => {
    let checkbox_counts = {
        "brands": {},
        "types": {},
        "axes": {},
        "applications": {},
    };

    Object.entries(robots_list).map((robot) => {
        const [key, entry] = [robot[0], robot[1]];

        // count brands
        if (checkbox_counts["brands"].hasOwnProperty(entry["B"]))
            checkbox_counts["brands"][entry["B"]] += 1;
        else
            checkbox_counts["brands"][entry["B"]] = 1;

        // count types
        if (checkbox_counts["types"].hasOwnProperty(entry["T"]))
            checkbox_counts["types"][entry["T"]] += 1;
        else
            checkbox_counts["types"][entry["T"]] = 1;

        // count axes
        if (checkbox_counts["axes"].hasOwnProperty(entry["A"]))
            checkbox_counts["axes"][entry["A"]] += 1;
        else
            checkbox_counts["axes"][entry["A"]] = 1;

        // count applications
        if (entry.hasOwnProperty("AP")) {
            entry["AP"].map((application) => {
                if (checkbox_counts["applications"].hasOwnProperty(application))
                    checkbox_counts["applications"][application] += 1;
                else
                    checkbox_counts["applications"][application] = 1;
            });
        };
    });

    return checkbox_counts;
};

const countToolCheckbox = (robots_list: Array<RobotDataProp>) => {
    let checkbox_counts = {
        "brands": {},
        "axes": {},
        "applications": {},
    };

    Object.entries(robots_list).map((robot) => {
        const [key, entry] = [robot[0], robot[1]];

        // count brands
        if (checkbox_counts["brands"].hasOwnProperty(entry["B"]))
            checkbox_counts["brands"][entry["B"]] += 1;
        else
            checkbox_counts["brands"][entry["B"]] = 1;

        // count axes
        if (checkbox_counts["axes"].hasOwnProperty(entry["A"]))
            checkbox_counts["axes"][entry["A"]] += 1;
        else
            checkbox_counts["axes"][entry["A"]] = 1;

        // count applications
        if (entry.hasOwnProperty("AP")) {
            entry["AP"].map((application) => {
                if (checkbox_counts["applications"].hasOwnProperty(application))
                    checkbox_counts["applications"][application] += 1;
                else
                    checkbox_counts["applications"][application] = 1;
            });
        };
    });

    return checkbox_counts;
};

const countAxisCheckbox = (robots_list: Array<RobotDataProp>) => {
    let checkbox_counts = {
        "brands": {},
        "axes": {},
    };

    Object.entries(robots_list).map((robot) => {
        const [key, entry] = [robot[0], robot[1]];

        // count brands
        if (checkbox_counts["brands"].hasOwnProperty(entry["B"]))
            checkbox_counts["brands"][entry["B"]] += 1;
        else
            checkbox_counts["brands"][entry["B"]] = 1;

        // count axes
        if (checkbox_counts["axes"].hasOwnProperty(entry["A"]))
            checkbox_counts["axes"][entry["A"]] += 1;
        else
            checkbox_counts["axes"][entry["A"]] = 1;
    });

    return checkbox_counts;
};

const countObjectCheckbox = (robots_list: Array<RobotDataProp>) => {
    let checkbox_counts = {
        "brands": {},
        "types": {},
        "axes": {},
        "applications": {},
    };

    Object.entries(robots_list).map((robot) => {
        const [key, entry] = [robot[0], robot[1]];

        // count brands
        if (checkbox_counts["brands"].hasOwnProperty(entry["B"]))
            checkbox_counts["brands"][entry["B"]] += 1;
        else
            checkbox_counts["brands"][entry["B"]] = 1;

        // count types
        if (checkbox_counts["types"].hasOwnProperty(entry["T"]))
            checkbox_counts["types"][entry["T"]] += 1;
        else
            checkbox_counts["types"][entry["T"]] = 1;

        // count axes
        if (checkbox_counts["axes"].hasOwnProperty(entry["A"]))
            checkbox_counts["axes"][entry["A"]] += 1;
        else
            checkbox_counts["axes"][entry["A"]] = 1;

        // count applications
        if (entry.hasOwnProperty("AP")) {
            entry["AP"].map((application) => {
                if (checkbox_counts["applications"].hasOwnProperty(application))
                    checkbox_counts["applications"][application] += 1;
                else
                    checkbox_counts["applications"][application] = 1;
            });
        };
    });

    return checkbox_counts;
};

const countStationCheckbox = (stations_list: any) => {
    let checkbox_counts = {
        "brands": {},
        "tags": {},
        "applications": {},
        "mechanism-type": {},
        "sizes": {
            "Small": 0,
            "Medium": 0,
            "Large": 0,
        },
    };

    Object.entries(stations_list).map((station) => {
        const [key, entry] = [station[0], station[1]];
        if (entry.hasOwnProperty("B")) {
            entry["B"].map((brand) => {
                if (checkbox_counts["brands"].hasOwnProperty(brand))
                    checkbox_counts["brands"][brand] += 1;
                else
                    checkbox_counts["brands"][brand] = 1;
            });
        };

        // count tags
        if (entry.hasOwnProperty("T")) {
            entry["T"].map((tag) => {
                if (checkbox_counts["tags"].hasOwnProperty(tag))
                    checkbox_counts["tags"][tag] += 1;
                else
                    checkbox_counts["tags"][tag] = 1;
            });
        };

        // count applications
        if (entry.hasOwnProperty("AP")) {
            entry["AP"].map((application) => {
                if (checkbox_counts["applications"].hasOwnProperty(application))
                    checkbox_counts["applications"][application] += 1;
                else
                    checkbox_counts["applications"][application] = 1;
            });
        };

        if (entry.hasOwnProperty("RT")) {
            entry["RT"].map((mechanism) => {
                if (checkbox_counts["mechanism-type"].hasOwnProperty(mechanism))
                    checkbox_counts["mechanism-type"][mechanism] += 1;
                else
                    checkbox_counts["mechanism-type"][mechanism] = 1;
            })
        }

        // count sizes
        if (entry.hasOwnProperty("S")) {
            if (
                entry["S"] > size_range["Small"][0] &&
                entry["S"] < size_range["Small"][1]
            )
                checkbox_counts["sizes"]["Small"] += 1;

            if (
                entry["S"] > size_range["Medium"][0] &&
                entry["S"] < size_range["Medium"][1]
            )
                checkbox_counts["sizes"]["Medium"] += 1;

            if (
                entry["S"] > size_range["Large"][0] &&
                entry["S"] < size_range["Large"][1]
            )
                checkbox_counts["sizes"]["Large"] += 1;
        }
    });

    return checkbox_counts;
};

const countAddinCheckbox = (addin_list: any) => {
    let checkbox_counts = {
        // "applications": {},
        "publishers": {},
        "categories": {},
        "price": {
            "Free": 0,
            "Paid": 0,
        }
    };

    Object.entries(addin_list).map((addin) => {
        const [key, entry] = [addin[0], addin[1]];

        // count publishers
        if (checkbox_counts["publishers"].hasOwnProperty(entry["P"]))
            checkbox_counts["publishers"][entry["P"]] += 1;
        else
            checkbox_counts["publishers"][entry["P"]] = 1;

        // count categories
        if (checkbox_counts["categories"].hasOwnProperty(entry["C"]))
            checkbox_counts["categories"][entry["C"]] += 1;
        else
            checkbox_counts["categories"][entry["C"]] = 1;

        // count free/paid addins
        if (entry.hasOwnProperty("PX"))
            checkbox_counts["price"]["Paid"] += 1;
        else
            checkbox_counts["price"]["Free"] += 1
    });

    return checkbox_counts;
};

const countPostCheckbox = (post_list: any) => {
    let checkbox_counts = {
        "brands": {},
        "controllers": {},
        "extensions": {},
    };

    Object.entries(post_list).map((post) => {
        const [key, entry] = [post[0], post[1]];

        // count brands
        if (entry.hasOwnProperty("B")) {
            if (checkbox_counts["brands"].hasOwnProperty(entry["B"]))
                checkbox_counts["brands"][entry["B"]] += 1;
            else
                checkbox_counts["brands"][entry["B"]] = 1;
        }

        // count controllers
        if (entry.hasOwnProperty("SC")) {
            for (let controller of entry["SC"]) {
                if (checkbox_counts["controllers"].hasOwnProperty(controller))
                    checkbox_counts["controllers"][controller] += 1;
                else
                    checkbox_counts["controllers"][controller] = 1;
            }
        }

        // count extensions
        if (entry.hasOwnProperty("EXT")) {
            if (checkbox_counts["extensions"].hasOwnProperty(entry["EXT"]))
                checkbox_counts["extensions"][entry["EXT"]] += 1;
            else
                checkbox_counts["extensions"][entry["EXT"]] = 1;
        }
    });

    return checkbox_counts;
};


export const FilterUtils = {
    // url query
    extracted_params,
    extractUrlQueryOnMessage,

    // methods
    applyFiltersToList,
    countCheckboxParameters,
    rangeFilterChangedFromDefault,
};